import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { ArrowLeft, Clock, User, MessageCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ShareButtons from '../components/ShareButtons';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const NewsDetail = () => {
  const { newsId } = useParams();
  const [news, setNews] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { user, isAuthenticated, login } = useAuth();

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchNews();
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsId]);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/news/posts/${newsId}`);
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/news/posts/${newsId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    if (!isAuthenticated) {
      toast.error('Please login to comment');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/news/posts/${newsId}/comments`,
        { content: newComment },
        { withCredentials: true }
      );
      setNewComment('');
      fetchComments();
      toast.success('Comment added');
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#007AFF]"></div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">News not found</h1>
          <Link to="/news">
            <Button className="bg-[#007AFF] text-white hover:bg-[#2E90FA]">
              Back to News
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Back Button */}
          <Link to="/news" className="inline-flex items-center text-gray-400 hover:text-[#007AFF] transition-colors mb-8" data-testid="back-button">
            <ArrowLeft size={20} className="mr-2" />
            Back to News
          </Link>

          {/* News Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="bg-[#007AFF]/20 text-[#007AFF] border border-[#007AFF]/30 text-xs px-2 py-1 rounded-full">
                {news.category}
              </span>
              {news.tags?.map((tag, idx) => (
                <span key={idx} className="bg-white/10 text-white text-xs px-2 py-1 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white mb-6" data-testid="news-title">
              {news.title}
            </h1>
            <p className="text-xl text-gray-300 mb-6">
              {news.summary}
            </p>
            <div className="flex items-center gap-6 text-gray-400 text-sm">
              <div className="flex items-center gap-2">
                <User size={16} />
                <span>{news.author_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={16} />
                <span>{new Date(news.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            </div>
            <div className="mt-5">
              <ShareButtons title={news.title} />
            </div>
          </div>

          {/* Featured Image */}
          {news.image && (
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={news.image}
                alt={news.title}
                className="w-full h-auto"
              />
            </div>
          )}

          {/* News Content */}
          <div className="prose prose-invert max-w-none" data-testid="news-content">
            <div className="text-gray-300 text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(news.content) }}
            />
          </div>

          {/* Comments Section */}
          <div className="mt-16 border-t border-white/10 pt-12">
            <h3 className="font-manrope font-bold text-2xl text-white mb-8 flex items-center gap-2">
              <MessageCircle size={24} className="text-[#007AFF]" />
              Discussion ({comments.length})
            </h3>

            {/* Comment Form */}
            {isAuthenticated ? (
              <div className="bg-[#111111] border border-white/10 rounded-lg p-6 mb-8">
                <form onSubmit={handleCommentSubmit}>
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Share your thoughts on this news..."
                    className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] rounded-sm mb-4 min-h-[100px]"
                    data-testid="comment-input"
                  />
                  <Button
                    type="submit"
                    disabled={submitting || !newComment.trim()}
                    className="bg-[#007AFF] text-white hover:bg-[#2E90FA] rounded-sm px-6 py-2 font-semibold"
                    data-testid="comment-submit"
                  >
                    {submitting ? 'Posting...' : 'Post Comment'}
                  </Button>
                </form>
              </div>
            ) : (
              <div className="bg-[#111111] border border-white/10 rounded-lg p-6 mb-8 text-center">
                <p className="text-gray-400 mb-4">Login to join the discussion</p>
                <Button
                  onClick={login}
                  className="bg-[#007AFF] text-white hover:bg-[#2E90FA] rounded-sm px-6 py-2 font-semibold"
                >
                  Login to Comment
                </Button>
              </div>
            )}

            {/* Comments List */}
            <div className="space-y-4">
              {comments.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No comments yet. Be the first to share your thoughts!</p>
              ) : (
                comments.map((comment) => (
                  <div
                    key={comment.comment_id}
                    className="bg-[#111111] border border-white/10 rounded-lg p-6"
                    data-testid={`comment-${comment.comment_id}`}
                  >
                    <div className="flex gap-4">
                      <Avatar className="h-10 w-10 flex-shrink-0">
                        <AvatarImage src={comment.author_picture} alt={comment.author_name} />
                        <AvatarFallback className="bg-[#007AFF] text-white font-semibold">
                          {comment.author_name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-white">{comment.author_name}</span>
                          <span className="text-gray-500 text-xs">
                            {new Date(comment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>
                        <p className="text-gray-300 whitespace-pre-wrap">{comment.content}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default NewsDetail;
